import Skeleton from '@sendsprint/ui-react/dist/components/Skeleton';
import Box from '@sendsprint/ui-react/dist/components/Box';
import Icon from '@sendsprint/ui-react/dist/components/Icon';
import useMedia from '@src/hooks/utils/useMedia';
import { lazy, useEffect } from 'react';
import SuspenseWrapper from '../suspenseWrapper';

const ChevronLeftOutline = lazy(() => import('@sendsprint/ui-react/dist/icons/ChevronLeftOutline'));
const ChevronRightOutline = lazy(
  () => import('@sendsprint/ui-react/dist/icons/ChevronRightOutline')
);

interface Props {
  currentPage: number;
  dataLength: number;
  postsPerPage?: number;
  displayedBtns?: number;
  currentPageFunc?: React.Dispatch<React.SetStateAction<number>>;
  onNavigate?: (val: number) => void;
  variant?: 'client' | 'backend';
  isLoading?: boolean;
  isFetching?: boolean;
}

const Pagination: React.FC<Props> = ({
  currentPage = 1,
  currentPageFunc,
  dataLength,
  postsPerPage = 10,
  displayedBtns = 5,
  onNavigate,
  variant = 'client',
  isFetching,
  isLoading
}) => {
  const pageNumbers: number[] = [];
  const mappedBtnsNumbers: number[] = [];
  const { isMobile } = useMedia();

  const highestNumber = Math.ceil(dataLength / postsPerPage);

  for (let i = 1; i <= highestNumber; i++) {
    pageNumbers.push(i);
  }

  displayedBtns = isMobile ? 3 : displayedBtns;

  useEffect(() => {
    const paginationIcons = document.querySelectorAll('.pagination__page');

    for (let i = 0; i < paginationIcons.length; i++) {
      paginationIcons[i].classList.remove('ss-bg-primary1-500');
      paginationIcons[i].classList.remove('ss-text-white');

      if (Number(paginationIcons[i].innerHTML) === currentPage) {
        paginationIcons[i].classList.add('ss-bg-primary1-500');
        paginationIcons[i].classList.add('ss-text-white');
      }
    }
  }, [currentPage, isLoading]);

  const handlePagination = (e: React.MouseEvent<HTMLElement>, pageNumber: number) => {
    if (variant === 'client' && currentPageFunc) {
      currentPageFunc(pageNumber);
    }

    if (variant === 'backend' && onNavigate) {
      onNavigate(pageNumber);
    }

    const paginationIcons = document.querySelectorAll('.pagination__page');

    paginationIcons.forEach((icon) => {
      icon.classList.remove('ss-bg-primary1-500');
      icon.classList.remove('ss-text-white');
    });

    e.currentTarget.classList.add('ss-text-white');
    e.currentTarget.classList.add('ss-bg-primary1-500');
  };

  const nextPagination = () => {
    if (variant === 'client' && currentPageFunc) {
      if (currentPage === highestNumber) {
        return currentPageFunc((current: number) => current + 0);
      }
      currentPageFunc((current: number) => current + 1);
    }

    if (variant === 'backend' && onNavigate) {
      if (currentPage === highestNumber) {
        return onNavigate(currentPage + 0);
      }

      onNavigate(currentPage + 1);
    }

    const paginationIcons = document.querySelectorAll('.pagination__page');

    for (let i = 0; i < paginationIcons.length; i++) {
      paginationIcons[i].classList.remove('ss-bg-primary1-500');
      paginationIcons[i].classList.remove('ss-text-white');

      if (Number(paginationIcons[i].innerHTML) === currentPage + 1) {
        paginationIcons[i].classList.add('ss-bg-primary1-500');
        paginationIcons[i].classList.add('ss-text-white');
      }
    }
  };

  const prevPagination = () => {
    if (variant === 'client' && currentPageFunc) {
      if (currentPage <= 1) {
        return currentPageFunc((current: number) => current - 0);
      }
      currentPageFunc((current: number) => current - 1);
    }

    if (variant === 'backend' && onNavigate) {
      if (currentPage <= 1) {
        return onNavigate(currentPage - 0);
      }
      onNavigate(currentPage - 1);
    }

    const paginationIcons = document.querySelectorAll('.pagination__page');

    for (let i = 0; i < paginationIcons.length; i++) {
      paginationIcons[i].classList.remove('ss-bg-primary1-500');
      paginationIcons[i].classList.remove('ss-text-white');

      if (Number(paginationIcons[i].innerHTML) === currentPage - 1) {
        paginationIcons[i].classList.add('ss-bg-primary1-500');
        paginationIcons[i].classList.add('ss-text-white');
      }
    }
  };

  const handlePaginate = (num: number) => {
    if (variant === 'client' && currentPageFunc) {
      currentPageFunc(num);
    }

    if (variant === 'backend' && onNavigate) {
      onNavigate(num);
    }
  };

  let maxLeft = currentPage - Math.floor(displayedBtns / 2);
  let maxRight = currentPage + Math.floor(displayedBtns / 2);

  if (maxLeft < 1) {
    maxLeft = 1;
    maxRight = displayedBtns;
  }

  if (maxRight > highestNumber) {
    maxRight = highestNumber;

    maxLeft = highestNumber - (displayedBtns - 1);

    if (maxLeft < 1) {
      maxLeft = 1;
    }
  }

  for (let i = maxLeft; i <= maxRight; i++) {
    mappedBtnsNumbers.push(i);
  }

  if (isLoading) return null;

  return (
    <Box className="ss-flex ss-items-center ss-bg-neutral-5 ss-p-3 ss-rounded">
      {/* <Box
        className="pagination__prev ss-mr-2 ss-cursor-pointer"
        onClick={() => handlePaginate(1)}
      >
        <span className="pagination__nextText">
          <img src={arrowRight} alt="" />
        </span>
      </Box> */}
      <button
        className="pagination__prev ss-outline-none focus:ss-focus-ring ss-rounded ss-cursor-pointer disabled:ss-opacity-40 disabled:ss-cursor-not-allowed hover:ss-bg-primary1-50"
        onClick={prevPagination}
        disabled={currentPage === 1 || isFetching}>
        <span className="pagination__nextText">
          <SuspenseWrapper
            suspenseFallback={<Skeleton className="ss-w-8 ss-h-8 ss-rounded-full" />}
            returnNullErrorFallback>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <Icon svg={ChevronLeftOutline as any} size={30} />
          </SuspenseWrapper>
        </span>
      </button>
      {!mappedBtnsNumbers.includes(1) && (
        <>
          <button
            disabled={isFetching}
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onClick={(e) => handlePaginate(1)}
            className={`pagination__page hover:ss-bg-primary1-50 disabled:ss-opacity-40 disabled:ss-cursor-not-allowed ss-cursor-pointer ss-py-2 ss-px-4 ss-rounded`}
            // datanumber={number}
          >
            1
          </button>
          <Box className="ss-flex ss-items-center">
            <span className="ss-w-1 ss-h-1 ss-rounded ss-bg-primary1-400 ss-mx-0.5"></span>
            <span className="ss-w-1 ss-h-1 ss-rounded ss-bg-primary1-400 ss-mx-0.5"></span>
          </Box>
        </>
      )}
      {mappedBtnsNumbers !== undefined &&
        mappedBtnsNumbers.map((number) => {
          return (
            <button
              disabled={isFetching}
              onClick={(e) => handlePagination(e, number)}
              key={number}
              className={`pagination__page ss-cursor-pointer focus:ss-focus-ring ss-py-2 disabled:ss-opacity-40 disabled:ss-cursor-not-allowed ss-px-4 ss-rounded hover:ss-bg-primary1-50 
              `}>
              {number}
            </button>
          );
        })}
      {!mappedBtnsNumbers.includes(highestNumber) && (
        <>
          <Box className="ss-flex ss-items-center">
            <span className="ss-w-1 ss-h-1 ss-rounded ss-bg-primary1-400 ss-mx-0.5"></span>
            <span className="ss-w-1 ss-h-1 ss-rounded ss-bg-primary1-400 ss-mx-0.5"></span>
          </Box>
          <button
            disabled={isFetching}
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onClick={(e) => handlePaginate(highestNumber)}
            className={`pagination__page hover:ss-bg-primary1-50 disabled:ss-opacity-40 disabled:ss-cursor-not-allowed ss-cursor-pointer ss-py-2 ss-px-4 ss-rounded`}
            // datanumber={number}
          >
            {highestNumber}
          </button>
        </>
      )}
      <button
        className="pagination__next ss-outline-none focus:ss-focus-ring disabled:ss-opacity-40 disabled:ss-cursor-not-allowed ss-rounded ss-cursor-pointer hover:ss-bg-primary1-50"
        onClick={nextPagination}
        disabled={currentPage === highestNumber || isFetching}>
        <span className="pagination__nextText">
          <SuspenseWrapper
            suspenseFallback={<Skeleton className="ss-w-8 ss-h-8 ss-rounded-full" />}
            returnNullErrorFallback>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <Icon svg={ChevronRightOutline as any} size={30} />
          </SuspenseWrapper>
        </span>
      </button>
      {/* <Box
        className="pagination__next ss-ml-2 ss-cursor-pointer"
        onClick={() => handlePaginate(highestNumber)}
      >
        <span className="pagination__nextText">
          <img src={arrowRight} alt="" />
        </span>
      </Box> */}
    </Box>
  );
};

export default Pagination;
